// client/src/components/company/Markets.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ArrowUpDown, Percent, TrendingUp, Calendar, AlertCircle, Filter } from 'lucide-react';
import { useResponsive } from '../../hooks/useResponsive';

const Markets = () => {
  const { t, i18n } = useTranslation();
  const { isMobile } = useResponsive();
  const [rates, setRates] = useState([]);
  const [latestRate, setLatestRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Stato per la selezione
  const [selectedRateType, setSelectedRateType] = useState('euribor'); // 'euribor' o 'estr'
  const [selectedIndex, setSelectedIndex] = useState('euribor1M');
  const [timeRange, setTimeRange] = useState('1Y'); // 'ALL', '10Y', '5Y', '1Y', '6M', '3M'
  const [loadedYears, setLoadedYears] = useState(5); // Traccia quanti anni sono stati caricati
  const [isLoadingMoreData, setIsLoadingMoreData] = useState(false);

  // Opzioni degli indici EURIBOR
  const euriborOptions = [
    { value: 'euribor1W', label: 'Euribor 1W', color: '#0066CC' },
    { value: 'euribor1M', label: 'Euribor 1M', color: '#28A745' },
    { value: 'euribor3M', label: 'Euribor 3M', color: '#FFA500' },
    { value: 'euribor6M', label: 'Euribor 6M', color: '#DC3545' },
    { value: 'euribor12M', label: 'Euribor 12M', color: '#6F42C1' }
  ];

  // Opzioni degli indici €STR
  const estrOptions = [
    { value: 'estrON', label: '€STR', color: '#17A2B8' }
  ];

  // Opzioni ON rates altre valute
  const onRatesOptions = [
    { value: 'soniaON', label: 'SONIA (GBP)', color: '#E53935' },
    { value: 'sofrON',  label: 'SOFR (USD)',  color: '#1E88E5' },
    { value: 'aoniaON', label: 'AONIA (AUD)', color: '#43A047' }
  ];

  // Opzioni filtro temporale
  const timeRangeOptions = [
    { value: 'ALL', label: t('markets.timeRange.all', { defaultValue: 'All Data' }) },
    { value: '10Y', label: t('markets.timeRange.10y', { defaultValue: '10 Years' }) },
    { value: '5Y', label: t('markets.timeRange.5y', { defaultValue: '5 Years' }) },
    { value: '1Y', label: t('markets.timeRange.1y', { defaultValue: '1 Year' }) },
    { value: '6M', label: t('markets.timeRange.6m', { defaultValue: '6 Months' }) },
    { value: '3M', label: t('markets.timeRange.3m', { defaultValue: '3 Months' }) }
  ];

  // Ottiene le opzioni correnti in base al tipo selezionato
  const currentIndexOptions = selectedRateType === 'euribor'
    ? euriborOptions
    : selectedRateType === 'estr'
      ? estrOptions
      : selectedRateType === 'sonia'
        ? [onRatesOptions[0]]
        : selectedRateType === 'sofr'
          ? [onRatesOptions[1]]
          : selectedRateType === 'aonia'
            ? [onRatesOptions[2]]
            : euriborOptions;

  const fetchRates = useCallback(async (years = 5) => {
    try {
      setLoading(true);
      setError('');

      // Carica i dati con il parametro years
      const yearsParam = years === 0 ? 'all' : years;
      const response = await axios.get(`/api/markets/rates?years=${yearsParam}`);
      setRates(response.data);
      setLoadedYears(years);

      const latestResponse = await axios.get('/api/markets/rates/latest');
      setLatestRate(latestResponse.data);

      setLoading(false);
    } catch (err) {
      console.error('Error fetching rates:', err);
      if (err.response?.status === 403) {
        setError(t('markets.accessDenied'));
      } else {
        setError(t('markets.errorLoading'));
      }
      setLoading(false);
    }
  }, [t]);

  // Funzione per gestire il cambio del time range
  const handleTimeRangeChange = useCallback(async (newTimeRange) => {
    setTimeRange(newTimeRange);

    // Determina quanti anni servono per il nuovo range
    let yearsNeeded = 5; // default
    if (newTimeRange === 'ALL') {
      yearsNeeded = 0; // tutti i dati
    } else if (newTimeRange === '10Y') {
      yearsNeeded = 10;
    } else if (newTimeRange === '5Y') {
      yearsNeeded = 5;
    } else {
      // Per 3M, 6M, 1Y non serve ricaricare se abbiamo già 5 anni
      return;
    }

    // Se abbiamo bisogno di più dati di quelli già caricati, ricarica
    if (yearsNeeded === 0 || yearsNeeded > loadedYears) {
      try {
        setIsLoadingMoreData(true);
        const yearsParam = yearsNeeded === 0 ? 'all' : yearsNeeded;
        const response = await axios.get(`/api/markets/rates?years=${yearsParam}`);
        setRates(response.data);
        setLoadedYears(yearsNeeded);
        setIsLoadingMoreData(false);
      } catch (err) {
        console.error('Error fetching more data:', err);
        setIsLoadingMoreData(false);
      }
    }
  }, [loadedYears]);
  useEffect(() => {
     fetchRates();
   }, [fetchRates]);

  // Quando cambia il tipo di tasso, seleziona il primo indice disponibile
  useEffect(() => {
    if (selectedRateType === 'euribor') {
      setSelectedIndex('euribor1M');
    } else if (selectedRateType === 'estr') {
      setSelectedIndex('estrON');
    } else if (selectedRateType === 'sonia') {
      setSelectedIndex('soniaON');
    } else if (selectedRateType === 'sofr') {
      setSelectedIndex('sofrON');
    } else if (selectedRateType === 'aonia') {
      setSelectedIndex('aoniaON');
    }
  }, [selectedRateType]);

  // Prepara i dati per il grafico con filtro temporale
  const chartData = useMemo(() => {
    // Calcola la data di inizio in base al filtro temporale
    const getStartDate = () => {
      const now = new Date();
      switch (timeRange) {
        case '3M':
          return new Date(now.setMonth(now.getMonth() - 3));
        case '6M':
          return new Date(now.setMonth(now.getMonth() - 6));
        case '1Y':
          return new Date(now.setFullYear(now.getFullYear() - 1));
        case '5Y':
          return new Date(now.setFullYear(now.getFullYear() - 5));
        case '10Y':
          return new Date(now.setFullYear(now.getFullYear() - 10));
        case 'ALL':
        default:
          return null;
      }
    };

    const startDate = getStartDate();
    
    let filteredRates = [...rates]
      .filter(rate => {
        // Filtra per data
        if (startDate && new Date(rate.date) < startDate) return false;
        // Filtra solo record che hanno il valore selezionato
        return rate[selectedIndex] !== null && rate[selectedIndex] !== undefined;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    // Per dataset grandi, riduci i punti per performance
    if (filteredRates.length > 500) {
      const step = Math.ceil(filteredRates.length / 500);
      filteredRates = filteredRates.filter((_, index) => index % step === 0);
    }

   return filteredRates.map((rate, index) => {
    const rateDate = new Date(rate.date);
    let dateLabel;
    
    // Formattazione SOLO per visualizzazione asse X (può essere duplicata)
    switch (timeRange) {
      case '6M':
      case '3M':
      case '1Y':
        dateLabel = rateDate.toLocaleDateString(i18n.language === 'it' ? 'it-IT' : 'en-GB', {
          day: '2-digit',
          month: 'short',
          year: '2-digit'
        });
        break;
      default:
        dateLabel = rateDate.toLocaleDateString(i18n.language === 'it' ? 'it-IT' : 'en-US', {
          month: 'short',
          year: '2-digit'
        });
    }
    
    return {
      id: `${rate.date}-${index}`, // ✅ Chiave univoca per Recharts
      date: dateLabel, // Label per asse X (può essere duplicata)
      fullDate: rate.date, // Data completa per tooltip
      value: rate[selectedIndex]
    };
  });
  }, [rates, selectedIndex, timeRange, i18n.language]);

  // Prepara i dati per la tabella (ultimi 30 valori con dati disponibili)
const tableData = useMemo(() => {
  
  // Per EURIBOR e €STR, mostra ultimi 30 giorni (come prima)
  return [...rates]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 30);
}, [rates, selectedRateType]);

  // Formatta la data per la tabella
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'it' ? 'it-IT' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateShort = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'it' ? 'it-IT' : 'en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Formatta il valore percentuale
  const formatRate = (rate) => {
    if (rate === null || rate === undefined) return '-';
    return `${rate.toFixed(3)}%`;
  };

  // Tooltip personalizzato per il grafico
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          padding: '10px 14px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          <p style={{ margin: 0, fontWeight: 600, color: '#333', marginBottom: '4px' }}>
            {formatDateShort(payload[0].payload.fullDate)}
          </p>
          <p style={{ margin: 0, color: payload[0].color, fontWeight: 500 }}>
            {formatRate(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="company-analytics company-dashboard">
        <div className="loading">{t('common.loading', { defaultValue: 'Loading...' })}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="company-analytics company-dashboard">
        <div className="card">
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            padding: '40px 20px',
            color: '#757575'
          }}>
            <AlertCircle size={48} style={{ marginBottom: '16px', color: '#ea4335' }} />
            <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>{error}</h3>
            <p style={{ margin: 0 }}>{t('markets.contactSupport')}</p>
          </div>
        </div>
      </div>
    );
  }

  const selectedColor = currentIndexOptions.find(opt => opt.value === selectedIndex)?.color || '#0066CC';

  return (
    <div className="company-analytics company-dashboard">
      {/* Sezione Grafico */}
      <div className="card">
        <div className="card-title">
          <TrendingUp className="card-title-icon" size={20} />
          <span>{t('markets.historicalTrend', { defaultValue: 'Markets - Historical Trend' })}</span>
        </div>

        {/* Filtri */}
        <div style={{
          backgroundColor: '#ffffff',
          border: '2px solid #e3f2fd',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          {/* Riga 1: Tipo di tasso (Euribor / €STR) */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '12px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#424242'
            }}>
              <Filter size={16} style={{ marginRight: '8px' }} />
              {t('markets.selectRateType', { defaultValue: 'Rate Type' })}
            </label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setSelectedRateType('euribor')}
                style={{
                  padding: '12px 24px',
                  fontSize: '15px',
                  fontWeight: selectedRateType === 'euribor' ? '600' : '400',
                  border: selectedRateType === 'euribor' ? '2px solid #1a73e8' : '1px solid #bbdefb',
                  borderRadius: '8px',
                  backgroundColor: selectedRateType === 'euribor' ? '#e8f0fe' : '#ffffff',
                  color: selectedRateType === 'euribor' ? '#1a73e8' : '#424242',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                EURIBOR
              </button>
              <button
                onClick={() => setSelectedRateType('estr')}
                style={{
                  padding: '12px 24px',
                  fontSize: '15px',
                  fontWeight: selectedRateType === 'estr' ? '600' : '400',
                  border: selectedRateType === 'estr' ? '2px solid #17A2B8' : '1px solid #bbdefb',
                  borderRadius: '8px',
                  backgroundColor: selectedRateType === 'estr' ? '#e0f7fa' : '#ffffff',
                  color: selectedRateType === 'estr' ? '#17A2B8' : '#424242',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                €STR
              </button>
              {onRatesOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setSelectedRateType(opt.value.replace('ON', '').toLowerCase())}
                  style={{
                    padding: '12px 24px',
                    fontSize: '15px',
                    fontWeight: selectedRateType === opt.value.replace('ON', '').toLowerCase() ? '600' : '400',
                    border: selectedRateType === opt.value.replace('ON', '').toLowerCase() ? `2px solid ${opt.color}` : '1px solid #bbdefb',
                    borderRadius: '8px',
                    backgroundColor: selectedRateType === opt.value.replace('ON', '').toLowerCase() ? '#fce4ec' : '#ffffff',
                    color: selectedRateType === opt.value.replace('ON', '').toLowerCase() ? opt.color : '#424242',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {opt.label.split(' ')[0]}
                </button>
              ))}
            </div>
          </div>

          {/* Riga 2: Filtro temporale */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '12px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#424242'
            }}>
              <Calendar size={16} style={{ marginRight: '8px' }} />
              {t('markets.timeRange.label', { defaultValue: 'Time Range' })}
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {timeRangeOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => handleTimeRangeChange(option.value)}
                  disabled={isLoadingMoreData}
                  style={{
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: timeRange === option.value ? '600' : '400',
                    border: timeRange === option.value ? '2px solid #1a73e8' : '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: timeRange === option.value ? '#e8f0fe' : '#ffffff',
                    color: timeRange === option.value ? '#1a73e8' : '#666',
                    cursor: isLoadingMoreData ? 'wait' : 'pointer',
                    transition: 'all 0.2s',
                    opacity: isLoadingMoreData ? 0.6 : 1
                  }}
                >
                  {option.label}
                  {isLoadingMoreData && timeRange !== option.value && (option.value === 'ALL' || option.value === '10Y') && (
                    <span style={{ marginLeft: '4px' }}>...</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Riga 3: Selettore indice specifico */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '12px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#424242'
            }}>
              {t('markets.selectTenor', { defaultValue: 'Select Tenor' })}
            </label>
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '10px'
            }}>
              {currentIndexOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => setSelectedIndex(option.value)}
                  style={{
                    padding: '10px 16px',
                    fontSize: '14px',
                    fontWeight: selectedIndex === option.value ? '600' : '400',
                    border: selectedIndex === option.value ? `2px solid ${option.color}` : '1px solid #bbdefb',
                    borderRadius: '8px',
                    backgroundColor: selectedIndex === option.value ? '#f5f9ff' : '#ffffff',
                    color: selectedIndex === option.value ? option.color : '#424242',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span style={{
                    display: 'inline-block',
                    width: '12px',
                    height: '12px',
                    backgroundColor: option.color,
                    borderRadius: '3px'
                  }}></span>
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Grafico */}
        <div style={{ width: '100%', height: isMobile ? 300 : 450 }}>
          <ResponsiveContainer width="100%" height={isMobile ? 300 : 400}>
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              style={{ cursor: 'crosshair' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: isMobile ? 9 : 11 }}
                angle={isMobile ? -45 : -30}
                textAnchor="end"
                height={isMobile ? 60 : 50}
                interval={Math.ceil(chartData.length / (isMobile ? 8 : 15))}
              />
              <YAxis 
                tickFormatter={(value) => `${value.toFixed(2)}%`}
                tick={{ fontSize: isMobile ? 10 : 12 }}
                domain={['auto', 'auto']}
                label={{ 
                  value: t('markets.rate', { defaultValue: 'Rate (%)' }), 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { fontSize: isMobile ? 10 : 12 }
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: isMobile ? 11 : 13 }}
                iconType="line"
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={selectedColor}
                fill={selectedColor}
                fillOpacity={0.3}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6, strokeWidth: 2 }}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div style={{
          marginTop: '12px', 
          padding: '15px', 
          backgroundColor: '#f5f5f5', 
          borderRadius: '8px',
          fontSize: isMobile ? '12px' : '14px'
        }}>
          <p style={{ margin: 0, color: '#757575' }}>
            <strong>{t('markets.chartSource', { defaultValue: 'Source:' })}</strong>
            {' '}
            {selectedRateType === 'euribor' 
              ? t('markets.chartDescriptionEuribor', { 
                  defaultValue: 'European Money Markets Institute' 
                })
              : t('markets.chartDescriptionEstr', { 
                  defaultValue: 'European Central Bank' 
                })
            }
          </p>
        </div>
      </div>

      {/* Sezione Tassi Correnti - EURIBOR */}
      {latestRate && (
        <div className="card">
          <div className="card-title">
            <Percent className="card-title-icon" size={20} />
            <span>{t('markets.currentRatesEuribor', { date: formatDate(latestRate.date) })}</span>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5, 1fr)',
            gap: '16px'
          }}>
            {euriborOptions.map(option => (
              <div 
                key={option.value}
                style={{
                  backgroundColor: '#f5f9ff',
                  borderRadius: '12px',
                  padding: '16px',
                  borderLeft: `4px solid ${option.color}`,
                  transition: 'all 0.2s'
                }}
              >
                <h4 style={{ 
                  margin: '0 0 8px 0', 
                  fontSize: '12px', 
                  fontWeight: '500', 
                  color: '#757575',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {option.label}
                </h4>
                <p style={{ 
                  margin: 0, 
                  fontSize: '20px', 
                  fontWeight: '700', 
                  color: option.color 
                }}>
                  {formatRate(latestRate[option.value])}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sezione Tassi Correnti - €STR */}
      {latestRate && latestRate.estrON !== null && (
        <div className="card">
          <div className="card-title">
            <Percent className="card-title-icon" size={20} />
            <span>{t('markets.currentRatesEstr', { date: formatDate(latestRate.date) })}</span>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(6, 1fr)',
            gap: '16px'
          }}>
            {estrOptions.map(option => (
              <div 
                key={option.value}
                style={{
                  backgroundColor: '#e0f7fa',
                  borderRadius: '12px',
                  padding: '16px',
                  borderLeft: `4px solid ${option.color}`,
                  transition: 'all 0.2s'
                }}
              >
                <h4 style={{ 
                  margin: '0 0 8px 0', 
                  fontSize: '12px', 
                  fontWeight: '500', 
                  color: '#757575',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {option.label}
                </h4>
                <p style={{ 
                  margin: 0, 
                  fontSize: '20px', 
                  fontWeight: '700', 
                  color: option.color 
                }}>
                  {formatRate(latestRate[option.value])}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sezione Tassi Correnti - SONIA / SOFR / AONIA */}
      {latestRate && onRatesOptions.some(o => latestRate[o.value] !== null && latestRate[o.value] !== undefined) && (
        <div className="card">
          <div className="card-title">
            <Percent className="card-title-icon" size={20} />
            <span>{t('markets.currentRatesON', { defaultValue: 'ON Rates — SONIA / SOFR / AONIA' })}</span>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)',
            gap: '16px'
          }}>
            {onRatesOptions.map(option => (
              <div
                key={option.value}
                style={{
                  backgroundColor: '#fce4ec',
                  borderRadius: '12px',
                  padding: '16px',
                  borderLeft: `4px solid ${option.color}`,
                  transition: 'all 0.2s'
                }}
              >
                <h4 style={{
                  margin: '0 0 8px 0',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#757575',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {option.label}
                </h4>
                <p style={{
                  margin: 0,
                  fontSize: '20px',
                  fontWeight: '700',
                  color: option.color
                }}>
                  {formatRate(latestRate[option.value])}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabella Storica (ultimi 30 giorni) */}
      <div className="card">
        <div className="card-title">
          <Calendar className="card-title-icon" size={20} />
          <span>{t('markets.historicalData', { defaultValue: 'Historical Data (Last 30 Days)' })}</span>
        </div>

        {/* Tab per selezionare quale tabella mostrare */}
        <div style={{ 
          display: 'flex', 
          gap: '10px', 
          marginBottom: '20px',
          borderBottom: '1px solid #e0e0e0',
          paddingBottom: '15px'
        }}>
          <button
            onClick={() => setSelectedRateType('euribor')}
            style={{
              padding: '8px 20px',
              fontSize: '14px',
              fontWeight: selectedRateType === 'euribor' ? '600' : '400',
              border: 'none',
              borderBottom: selectedRateType === 'euribor' ? '3px solid #1a73e8' : '3px solid transparent',
              backgroundColor: 'transparent',
              color: selectedRateType === 'euribor' ? '#1a73e8' : '#666',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            EURIBOR
          </button>
          <button
            onClick={() => setSelectedRateType('estr')}
            style={{
              padding: '8px 20px',
              fontSize: '14px',
              fontWeight: selectedRateType === 'estr' ? '600' : '400',
              border: 'none',
              borderBottom: selectedRateType === 'estr' ? '3px solid #17A2B8' : '3px solid transparent',
              backgroundColor: 'transparent',
              color: selectedRateType === 'estr' ? '#17A2B8' : '#666',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            €STR
          </button>
          {onRatesOptions.map(opt => {
            const type = opt.value.replace('ON', '').toLowerCase();
            return (
              <button
                key={opt.value}
                onClick={() => setSelectedRateType(type)}
                style={{
                  padding: '8px 20px',
                  fontSize: '14px',
                  fontWeight: selectedRateType === type ? '600' : '400',
                  border: 'none',
                  borderBottom: selectedRateType === type ? `3px solid ${opt.color}` : '3px solid transparent',
                  backgroundColor: 'transparent',
                  color: selectedRateType === type ? opt.color : '#666',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {opt.label.split(' ')[0]}
              </button>
            );
          })}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                <th style={{ 
                  padding: '12px 16px', 
                  textAlign: 'left', 
                  fontWeight: '600',
                  borderBottom: '2px solid #e0e0e0',
                  whiteSpace: 'nowrap'
                }}>
                  {t('markets.date', { defaultValue: 'Date' })}
                </th>
                {currentIndexOptions.map(opt => (
                  <th 
                    key={opt.value}
                    style={{ 
                      padding: '12px 16px', 
                      textAlign: 'right', 
                      fontWeight: '600',
                      borderBottom: '2px solid #e0e0e0',
                      whiteSpace: 'nowrap',
                      color: opt.color
                    }}
                  >
                    {opt.label.replace('Euribor ', '').replace('€STR ', '')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((rate, index) => (
                <tr 
                  key={rate._id || index}
                  style={{ 
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e8f0fe'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#fafafa'}
                >
                  <td style={{ 
                  padding: '12px 16px', 
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: '500'
                }}>
                  {formatDateShort(rate.date)}
                </td>
                  {currentIndexOptions.map(opt => (
                    <td 
                      key={opt.value}
                      style={{ 
                        padding: '12px 16px', 
                        textAlign: 'right',
                        borderBottom: '1px solid #e0e0e0',
                        fontFamily: 'monospace',
                        fontSize: '14px'
                      }}
                    >
                      {formatRate(rate[opt.value])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          
          {tableData.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#757575'
            }}>
              <p>{t('markets.noData', { defaultValue: 'No data available' })}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Markets;